/**
 * `HarnessRow` — one standalone harness task as a top-level row in
 * the Team of Agents pane (peer to mission rows; rendered after the
 * "standalone harnesses" divider).
 *
 * Layout (2026-07-02 pane redesign, operator brief): status is carried
 * by the GLYPH ALONE (animated spinner while running; the old status
 * word was redundant chrome) — the localized status label lives on the
 * row tooltip + aria-label instead. Three content lines:
 *
 *   1. headline — friendly `title` (fallback harnessId) + pct chip
 *   2. essence  — run `summary` (or live progress step), clamped to
 *      2 lines instead of hard-truncated
 *   3. meta     — clock stamp · elapsed/duration (ticks live while
 *      running) · cost · model, in thin small type
 *
 * Per §I.4: clicking the row body focuses-self + opens the detail
 * dialog. Per §I.13: cancel icon-button with native `confirm()`
 * (hidden on terminal rows + during the optimistic canceling window).
 */
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/useUIStore";
import { useMissionsStore } from "@/stores/useMissionsStore";
import { formatClock, formatCost, formatElapsed, joinMeta, useNow } from "./row-time";
import { useHarnessSteps } from "@/hooks/useHarnessSteps";
import { ScheduledBadge } from "./ScheduledBadge";
import { CaseChip } from "./CaseChip";
import { StatusBullet } from "./StatusBullet";
import type { HarnessStep } from "./harness-steps";
import type { StandaloneHarnessTreeNode } from "./monitor-tree";
import type { HarnTaskStatus } from "@/api/lx-harn";
import { normalizeProgressPct } from "@/lib/agents/progress-pct";

export interface HarnessRowProps {
  node: StandaloneHarnessTreeNode;
  onCancel: (taskId: string) => Promise<void> | void;
  /** Resolved case title for `node.caseId`, when the registry knows it. */
  caseLabel?: string;
  /** The case currently on screen — drives the chip's emphasis. */
  activeCaseId?: string | null;
  /** False until the case registry has loaded — gates the unresolved
   *  diagnostic so it cannot fire on first paint. */
  caseRegistryLoaded?: boolean;
}

export function HarnessRow({
  node,
  onCancel,
  caseLabel,
  activeCaseId,
  caseRegistryLoaded,
}: HarnessRowProps) {
  const { t, i18n } = useTranslation();
  const focusedRowId = useMissionsStore((s) => s.focusedRowId);
  const focusRow = useMissionsStore((s) => s.focusRow);
  const setRightSidebarExpanded = useUIStore((s) => s.setRightSidebarExpanded);

  const openHarnessDetail = useUIStore((s) => s.openHarnessDetail);
  const focused = focusedRowId === `task:${node.taskId}`;
  const ref = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (focused && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [focused]);

  // Per ledger v2 relay: a user-cancelled harness reports
  // status:"failed" + error.code:"user_cancelled" on the LIVE record.
  // Surface "Canceled" (grey glyph/tooltip) rather than the misleading
  // "Failed", matching what the central ledger normalizes the row to.
  const wasUserCanceled =
    node.status === "failed" && node.errorCode === "user_cancelled";
  const isTerminal = node.status === "completed" || node.status === "failed";
  const isActive = !isTerminal && node.status !== "queued";
  const showCancel = !isTerminal && !node.isCanceling;

  // Live elapsed for in-flight rows; frozen duration for terminal ones.
  const now = useNow(isActive || node.isCanceling);
  const elapsedMs = isTerminal
    ? (node.durationMs ??
      (node.completedAt !== undefined ? node.completedAt - node.createdAt : undefined))
    : now - node.createdAt;

  // The status WORD lives on tooltip + aria only (glyph carries it visually).
  const statusLabel = node.isCanceling
    ? t("rightSidebar.canceling")
    : wasUserCanceled
      ? t("rightSidebar.harnessStatus.canceled")
      : t(`rightSidebar.harnessStatus.${node.status}`);

  const meta = joinMeta(
    formatClock(node.createdAt, i18n.language),
    elapsedMs !== undefined ? formatElapsed(elapsedMs) : undefined,
    formatCost(node.costUsd),
    node.model,
  );

  // §K-bis: body click opens the detail dialog. The prior §I.4
  // self-focus is preserved via the dialog's natural focus, and we
  // still record the focused row id + ensure the sidebar is open
  // before the dialog mounts (so the row underneath stays visible
  // after close).
  const handleRowClick = () => {
    setRightSidebarExpanded(true);
    focusRow(`task:${node.taskId}`);
    openHarnessDetail(node.taskId);
  };

  // Sub-steps (redesign §4 option (a)): expanded by default while the
  // task is live — that is exactly when "what is it doing?" is the
  // question. Terminal rows collapse but stay expandable.
  const [expanded, setExpanded] = useState<boolean>(isActive);
  const { steps } = useHarnessSteps(
    node.taskId,
    node.status,
    expanded,
    // Re-derive whenever the newest frame moves.
    `${node.progress?.step ?? ""}:${node.progress?.pct ?? ""}`,
  );
  const hasSteps = steps.length > 0;

  const toggleExpanded = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  const handleCancel = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      t("rightSidebar.confirmCancelHarness", {
        harnessId: node.title ?? node.harnessId,
      }),
    );
    if (!confirmed) return;
    await onCancel(node.taskId);
  };

  return (
    <li
      ref={ref}
      data-testid={`harness-row-${node.taskId}`}
      data-status={node.status}
      data-focused={focused ? "true" : undefined}
      className={cn(
        "border-b border-cream-200/60 last:border-b-0 transition-colors dark:border-coffee-800/60",
        focused && "bg-blue-50 dark:bg-blue-950/30",
      )}
    >
      <button
        type="button"
        onClick={handleRowClick}
        title={statusLabel}
        aria-label={`${t("rightSidebar.harnessRowAriaLabel", {
          harnessId: node.title ?? node.harnessId,
        })} — ${statusLabel}`}
        className={cn(
          "flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-cream-50 dark:hover:bg-coffee-900/60",
          node.status === "awaiting_user" && "bg-amber-50/50 dark:bg-amber-950/15",
        )}
      >
        <span
          data-testid={`harness-row-expand-${node.taskId}`}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t("rightSidebar.collapseStepsAriaLabel")
              : t("rightSidebar.expandStepsAriaLabel")
          }
          onClick={toggleExpanded}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleExpanded(e);
            }
          }}
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-cream-100 hover:text-slate-700 dark:hover:bg-coffee-800 dark:hover:text-slate-200"
        >
          {expanded ? (
            <ChevronDown size={12} aria-hidden />
          ) : (
            <ChevronRight size={12} aria-hidden />
          )}
        </span>
        <HarnessStatusGlyph
          status={node.status}
          canceling={node.isCanceling}
          userCanceled={wasUserCanceled}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {/* Per 2026-06-23 relay: `title` (friendly harness name) is
                the row HEADLINE; old records without one fall back to
                the raw harnessId. */}
            <span
              className="truncate text-[11px] font-normal leading-tight text-slate-500 dark:text-slate-400"
              title={node.title ?? node.harnessId}
            >
              {node.title ?? node.harnessId}
            </span>
            {normalizeProgressPct(node.progress?.pct) !== null && !isTerminal && (
              <span className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
                {normalizeProgressPct(node.progress?.pct)}%
              </span>
            )}
          </div>
          {/* Essence: run summary (terminal) or the live progress step
              (in-flight). Two clamped lines — readable, not truncated
              to uselessness. */}
          {(node.summary || (node.progress?.step && !isTerminal)) && (
            <div
              className="mt-0.5 line-clamp-2 text-[11px] font-light leading-snug text-slate-600 dark:text-slate-300"
              title={node.summary ?? node.progress?.step}
            >
              {node.summary ?? node.progress?.step}
            </div>
          )}
          {/* Badge rides the meta line, not the headline: beside the title
              it stole the width the harness name needs. */}
          {(meta || node.origin === "scheduled" || node.caseId) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              {/* Org-scoped runs have no case by design and simply show
                  none — a row either names its case or says nothing. */}
              {node.caseId && (
                <CaseChip
                  testId={`harness-row-case-${node.taskId}`}
                  caseId={node.caseId}
                  label={caseLabel}
                  isActiveCase={node.caseId === activeCaseId}
                  registryLoaded={caseRegistryLoaded}
                />
              )}
              {node.origin === "scheduled" && (
                <ScheduledBadge testId={`harness-row-scheduled-${node.taskId}`} />
              )}
              {meta && (
                <span
                  data-testid={`harness-row-meta-${node.taskId}`}
                  className="text-[10px] font-light tabular-nums tracking-wide text-slate-400 dark:text-slate-500"
                >
                  {meta}
                </span>
              )}
            </div>
          )}
        </div>
        {showCancel && (
          <span
            data-testid={`harness-row-cancel-${node.taskId}`}
            role="button"
            tabIndex={0}
            aria-label={t("rightSidebar.cancelHarnessAriaLabel", {
              harnessId: node.title ?? node.harnessId,
            })}
            title={t("rightSidebar.cancelHarnessAriaLabel", {
              harnessId: node.title ?? node.harnessId,
            })}
            onClick={handleCancel}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                void handleCancel(e);
              }
            }}
            className="flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400"
          >
            <Trash2 size={12} />
          </span>
        )}
      </button>
      {expanded && hasSteps && (
        <ul
          data-testid={`harness-row-steps-${node.taskId}`}
          className="bg-cream-50/40 pl-8 dark:bg-coffee-950/30"
        >
          {steps.map((step) => (
            <HarnessStepRow key={step.step} taskId={node.taskId} step={step} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** One derived sub-step of a standalone harness. Mirrors the mission
 *  leaf row's shape so both planes read as the same checklist. */
function HarnessStepRow({
  taskId,
  step,
}: {
  taskId: string;
  step: HarnessStep;
}) {
  const detail = step.message;
  return (
    <li
      data-testid={`harness-step-${taskId}-${step.step}`}
      data-status={step.status}
      className="flex items-start gap-2 border-l border-cream-200/40 py-1 pl-3 pr-3 text-[11px] dark:border-coffee-800/40"
    >
      <HarnessStepGlyph status={step.status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-1.5">
          <span
            className={cn(
              "line-clamp-2 min-w-0 flex-1 font-light leading-snug",
              step.status === "done"
                ? "text-slate-500 dark:text-slate-500"
                : "text-slate-700 dark:text-slate-300",
            )}
          >
            {step.step}
          </span>
          {step.status === "running" && step.pct !== undefined && (
            <span className="shrink-0 text-[10px] tabular-nums text-blue-600/90 dark:text-blue-400/90">
              {step.pct}%
            </span>
          )}
        </div>
        {step.status === "running" && detail && (
          <div className="mt-0.5 truncate text-[10px] text-blue-600/90 dark:text-blue-400/90">
            {detail}
          </div>
        )}
      </div>
    </li>
  );
}

/** Bare stroke glyphs, matching `LeafStatusGlyph` — a step looks the
 *  same whether it belongs to a mission or to a standalone harness, and
 *  in both cases differs from its circled parent row. */
function HarnessStepGlyph({ status }: { status: HarnessStep["status"] }) {
  switch (status) {
    case "running":
      return <StatusBullet status="processing" size={6} />;
    case "failed":
      return <StatusBullet status="failed" size={6} />;
    default:
      return <StatusBullet status="done" size={6} />;
  }
}

function HarnessStatusGlyph({
  status,
  canceling,
  userCanceled,
}: {
  status: HarnTaskStatus;
  canceling: boolean;
  userCanceled?: boolean;
}) {
  if (canceling || userCanceled) return <StatusBullet status="canceled" />;
  switch (status) {
    case "queued":
      return <StatusBullet status="pending" />;
    case "dispatched":
    case "running":
      return <StatusBullet status="processing" />;
    case "awaiting_user":
      return <StatusBullet status="awaiting" />;
    case "completed":
      return <StatusBullet status="done" />;
    case "failed":
      return <StatusBullet status="failed" />;
    default:
      return <StatusBullet status="pending" />;
  }
}
